import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { StatusBadge } from './StatusBadge';

export interface SaveBarProps {
  /** Something differs from what was loaded. */
  dirty: boolean;
  saving?: boolean;
  /** Holds Save back (a required field is empty…); say why in `hint`. */
  disabled?: boolean;
  onSave: () => void;
  /** "Save changes", "Create vendor"… Defaults to Save. */
  saveLabel?: ReactNode;
  /** Replaces the saved / unsaved line, e.g. "A name is required". */
  hint?: ReactNode;
  /** Shown while dirty: puts the form back as it was loaded. */
  onDiscard?: () => void;
  /** Always shown: leave without saving (back to the list). */
  onCancel?: () => void;
  testId?: string;
  saveTestId?: string;
  className?: string;
}

/**
 * The one Save every admin editor uses: pinned to the bottom of the scroll so
 * it stays in reach on long forms, and saying what is at stake.
 */
export function SaveBar({
  dirty,
  saving,
  disabled,
  onSave,
  saveLabel,
  hint,
  onDiscard,
  onCancel,
  testId = 'save-bar',
  saveTestId,
  className,
}: SaveBarProps) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        'sticky bottom-4 z-20 flex flex-col gap-3 rounded-xl bg-card px-4 py-3 ring-1 ring-foreground/10 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
      data-testid={testId}
    >
      <div className="text-sm" aria-live="polite" data-testid={`${testId}-status`}>
        {hint ? (
          <span className="text-muted-foreground">{hint}</span>
        ) : dirty ? (
          <StatusBadge tone="warning" className="text-sm">{t('ADMIN_SHELL.COMMON.UNSAVED')}</StatusBadge>
        ) : (
          <span className="text-muted-foreground">{t('ADMIN_SHELL.COMMON.ALL_SAVED')}</span>
        )}
      </div>
      <div className="flex items-center justify-end gap-2">
        {onDiscard && dirty && (
          <Button variant="outline" onClick={onDiscard} disabled={saving} data-testid={`${testId}-discard`}>
            {t('ADMIN_SHELL.COMMON.DISCARD')}
          </Button>
        )}
        {onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={saving} data-testid={`${testId}-cancel`}>
            {t('ADMIN_SHELL.COMMON.CANCEL')}
          </Button>
        )}
        <Button onClick={onSave} disabled={saving || disabled} data-testid={saveTestId}>
          {saving && <Loader2 className="size-4 animate-spin" />}
          {saveLabel ?? t('ADMIN_SHELL.COMMON.SAVE')}
        </Button>
      </div>
    </div>
  );
}
